import styled, { css } from 'styled-components';
import { DisplayCheck } from './form/InputCheck';
import {
  FormattedCurrency,
  FormattedDate,
  FormattedNumber,
  FormattedPercent
} from '../../common/utils/formatters';
import type { ReactElement, ReactNode } from 'react';
import type { PlainObject } from 'simplytyped';

//@TODO, should be an enum
export type TableCellType =
  | 'text'
  | 'currency'
  | 'percent'
  | 'percent*100'
  | 'number'
  | 'table'
  | 'check'
  | 'array'
  | 'date'
  | 'idAndDate'
  | 'address'
  | 'hr'
  | 'error';

export type TTableCellContent =
  | {
      contentType: 'text';
      content: string | ReactNode | ReactElement | null | undefined;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'currency';
      content: number | string | 'NO_DATA' | undefined;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'percent';
      options?: PlainObject;
      content: number | undefined;
      breakAll?: boolean;
    }
  | {
      contentType: 'percent*100';
      options?: PlainObject;
      content: number | undefined;
      breakAll?: boolean;
    }
  | {
      contentType: 'range';
      options?: PlainObject;
      content: {
        min: number;
        max: number;
      };
      breakAll?: boolean;
    }
  | {
      contentType: 'rangeCurrency';
      options?: PlainObject;
      content: {
        min: number;
        max: number;
      };
      breakAll?: boolean;
    }
  | {
      contentType: 'number';
      options?: PlainObject;
      content: number | 'NO_DATA' | string | null | undefined;
      breakAll?: boolean;
    }
  | {
      contentType: 'table';
      content: Array<Array<TTableCellContent>>;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'check';
      content: boolean | undefined;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'array';
      content: ReadonlyArray<string | number | boolean>;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'date';
      content: string | undefined;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'address';
      content: string | undefined;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'hr';
      content?: undefined;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'error';
      content: string;
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'idAndDate';
      content: [string, string | undefined, boolean?];
      options?: PlainObject;
      breakAll?: boolean;
    }
  | {
      contentType: 'jsx';
      content: ReactElement;
      options?: PlainObject;
      breakAll?: boolean;
    };

const IdAndDateWrapper = styled.div`
  font-weight: 500;
`;

const IdWrapper = styled.div`
  text-align: left;
  word-break: break-word;
`;
const DateWrapper = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.main.accent};
    font-size: ${theme.fonts.sizes.h6};
  `
);

const ErrorWrapper = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.darkGrey};
    font-size: ${theme.fonts.sizes.h3};
    font-weight: 600;
  `
);

const IncompleteLabel = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.darkGrey};
    background-color: ${theme.colors.warmGrey};
    border-radius: ${theme.borderRadii.status};
    font-size: 0.75rem;
    padding: 2px 5px;
    display: inline-block;
    text-align: center;
  `
);

// @TODO, TTableCellContent should be a union type of valid type + content pairs
export const TableCellContent = ({
  contentType,
  content,
  options
}: TTableCellContent) => {
  return (
    <>
      {contentType === 'text' && content}
      {contentType === 'jsx' && content}
      {contentType === 'number' && (
        <FormattedNumber value={content as number} />
      )}
      {contentType === 'currency' && typeof content === 'number' && (
        <FormattedCurrency amount={content} />
      )}
      {contentType === 'percent' && typeof content === 'number' && (
        <FormattedPercent value={content} {...options} />
      )}
      {contentType === 'percent*100' && typeof content === 'number' && (
        <FormattedPercent value={(content || 0) / 100} {...options} />
      )}
      {contentType === 'range' && (
        <span>
          <FormattedNumber value={content.min} />
          {content.max === -1 ? (
            <span> +</span>
          ) : (
            <>
              <span> - </span>
              <FormattedNumber value={content.max} />
            </>
          )}
        </span>
      )}
      {contentType === 'rangeCurrency' && (
        <span>
          <FormattedCurrency amount={content.min} />
          {content.max === -1 ? (
            <span> +</span>
          ) : (
            <>
              <span> - </span>
              <FormattedCurrency amount={content.max} />
            </>
          )}
        </span>
      )}
      {contentType === 'check' && <DisplayCheck value={content as boolean} />}
      {contentType === 'array' && Array.isArray(content) && (
        <>{`${content.join(', ')} `}</>
      )}
      {contentType === 'date' && typeof content === 'string' && (
        <FormattedDate date={content} />
      )}
      {contentType === 'idAndDate' && Array.isArray(content) && (
        <IdAndDateWrapper>
          <IdWrapper>{content[0]}</IdWrapper>

          {typeof content[1] === 'string' && (
            <DateWrapper>
              <FormattedDate date={content[1]} />
            </DateWrapper>
          )}

          {content[2] && <IncompleteLabel>Missing data</IncompleteLabel>}
        </IdAndDateWrapper>
      )}
      {contentType === 'error' && <ErrorWrapper>{content}</ErrorWrapper>}
    </>
  );
};
